import { CreationSource } from '../store/mst/models/CreationSourceEnum';

export namespace AnalyticsEventsProps {
  export interface DefaultEventProps {
    userId?: string | null,
    name?: string | null,
    email?: string | null,
    orgId?: string | null,
    orgName?: string | null,
    legacyUuid?: string | null,
    gclid?: string | null,
    experiments?: Record<string, string> | null,
  }

  export namespace UI {
    export interface PublishEventProps {
      displaySummary: boolean,
    }

    export interface ScheduleAllEventProps {
      workspaceOptimizationPreferences: Record<string, any> | null
    }

    export type UIEventProps = PublishEventProps | ScheduleAllEventProps;
  }

  export namespace User {
    export interface SignedUpEventProps {
      signUpMethod: AuthenticationMethod,
      joinCompany: boolean,
    }

    export interface SignedInEventProps {
      loginMethod: AuthenticationMethod
    }

    export interface EmailVerifiedEventProps {
      verificationMethod?: VerificationMethod
    }

    export type UserEventProps = SignedUpEventProps | SignedInEventProps | EmailVerifiedEventProps;

    export enum AuthenticationMethod {
      MANUAL = 'EMAIL/PASS',
      GOOGLE = 'GOOGLE',
      APPLE  = 'APPLE',
    }

    export enum VerificationMethod {
      CODE = 'code',
    }
  }

  export namespace Order {
    export interface OrderFileUploadedEventProps {
      fileType: string,
      numRowsWithErrors: number,
      numAddressErrors: number,
      numTimeWindowErrors: number,
      numDurationErrors: number,
      numPhoneNumberErrors: number,
      numDeliveryDateErrors: number,
      totalRowCount: number,
    }

    export interface ModalViewedEventProps {
      modalName: string,
      viewName: string
    }

    export interface OrderImportColumnsMappedEventProps {
      mappedColumnCount: number,
      totalColumnCount: number,
      numColumnManuallyMapped: number,
    }

    export interface OrderImportCancelledEventProps {
      step: string,
    }

    export interface OrderImportConfirmedWithErrorsEventProps {
      totalValidRows: number,
      totalRowsWithErrors: number,
      totalRowCount: number,
    }

    export interface CustomFieldCreatedAndMappedEventProps {
      customFieldName: string;
      mappedColumnName: string;
    }

    export interface CSVDateFormatDetectedEventProps {
      totalOrdersWithDeliveryDate: number,
      percentOrdersWithYearFirst: number,
      percentOrdersWithMonthFirst: number,
      percentOrdersWithDayFirst: number,
      percentOrdersWithInvalidDate: number,
      preferredDateFormat: string,
      resultType: string,
    }

    export interface OrdersFilteredEventProps {
      searchTermLength: number,
      resultCount: number,
      appliedOrderStatuses: string[],
      hasScheduledDateFilter: boolean,
      hasDeliveryDateFilter: boolean,
      hasDriverNotesFilter: boolean,
      hasCustomerNameFilter: boolean,
      hasAddressIssuesFilter: boolean,
      hasCreatedDateFilter: boolean,
      scheduledDateRangeSizeDays?: number,
      scheduledDateRangeStartOffsetDays?: number,
      deliveryDateRangeSizeDays?: number,
      deliveryDateRangeStartOffsetDays?: number,
      createdDateRangeSizeDays?: number,
      createdDateRangeStartOffsetDays?: number,
      debugSessionUuid?: string | null,
    }

    export type OrderEventProps =
      | OrderFileUploadedEventProps
      | ModalViewedEventProps
      | OrderImportColumnsMappedEventProps
      | OrderImportCancelledEventProps
      | OrderImportConfirmedWithErrorsEventProps
      | CustomFieldCreatedAndMappedEventProps
      | CSVDateFormatDetectedEventProps
      | OrdersFilteredEventProps;

    export enum ModalNames {
      ORDER_UPLOAD = 'Order Upload Modal',
      SINGLE_ORDER = 'Single Order Modal',
    }

    export enum ModalViewNames {
      DATA_MAPPING = 'Data Mapping View',
      ERROR_FIXING = 'Error Fixing View',
      CREATE = 'Create View',
      EDIT = 'Edit View',
    }
  }

  export namespace Route {
    export enum DrawRouteEventTpe {
      SUBMITTED = 'Submitted',
      CANCELLED = 'Cancelled',
    }

    export interface DrawRouteEventProps {
      eventType: DrawRouteEventTpe
      routeId?: string
    }

    export interface AssignDriverToRouteEventProps {
      routeId: string
      driverId: string
      routeStatus: string
      driverFamiliarityScore?: number
      driverFamiliarityRating?: number
      driverFamiliarityRanking?: number
      driverSpeed?: number
      availableDrivers: number
    }

    export enum RouteSetupReuseEntrypoint {
      ADD_ROUTE_MENU = 'Add route menu',
      ROUTE_SETUP_PANEL = 'Route setup panel',
      QUICK_ADD_MENU = 'Quick add menu',
    }

    export interface RouteSetupReusedEventProps {
      currentDate: string
      selectedDate: string
      lookbackDays: number
      isSameWeekday: boolean
      entrypoint: RouteSetupReuseEntrypoint
    }

    export interface RouteEditedEventProps {
      routeId: string
      /** Whether the route was published at the moment the edit was saved. */
      isPublished: boolean
      /** Whether the driver had operationally started the route when the edit was saved. */
      isStarted: boolean
      editedFields: Array<string>
    }

    export type RouteSetupCardKey =
      | 'reuseDay'
      | 'autoCreate'
      | 'addFromTemplates'
      | 'createFromScratch';

    export enum RouteSetupOption {
      CREATE_MANUALLY = 'createManually',
      AUTO_CREATE = 'autoCreate',
      ADD_FROM_TEMPLATES = 'addFromTemplates',
      REUSE_DAY = 'reuseDay',
    }

    export interface RouteSetupOptionSelectedEventProps {
      option: RouteSetupOption
      activeTemplateCount: number
      inactiveTemplateCount: number
      hasPastRoutes: boolean
    }

    export type RouteEventProps =
      | DrawRouteEventProps
      | AssignDriverToRouteEventProps
      | RouteSetupReusedEventProps
      | RouteSetupOptionSelectedEventProps
      | RouteEditedEventProps;
  }

  export namespace Scheduling {
    export interface SchedulingRequestedEventProps {
      orderSelection: OrderSelection,
      orderSelectionCount: number | null,
      routeScenario: RouteScenario,
      routeScenarioCount: number,
      optimizationType: OptimizationType,
      source_page: Source,
    }

    export enum OrderSelection {
      ALL = 'all',
      SELECTION = 'selection',
    }

    export enum RouteScenario {
      ALL = 'all', 
      SELECTION = 'selection',
      NONE = 'none',
    }

    export enum OptimizationType {
      INSERT = 'insert',
      REOPTIMIZE = 'reoptimize',
    }

    // Which page the optimization was requested from.
    export enum Source {
      ORDERS = 'orders',
      ROUTES = 'routes',
    }

    export type SchedulingEventProps = SchedulingRequestedEventProps;
  }

  export namespace Shopify {
    export interface OrdersCreatedEventProps {
      creationSource: CreationSource
      importedCount: number
      skippedCount: number
    }

    export type ShopifyEventProps = OrdersCreatedEventProps;
  }

  export namespace CustomerNotifications {
    export enum TemplateType {
      EMAIL = 'Email',
      SMS = 'SMS',
    }

    export enum TemplateEditResult {
      APPROVED = 'Approved',
      REJECTED = 'Rejected',
    }

    export interface TemplateEditedEventProps {
      userId: string;
      company: string;
      type: TemplateType;
      result: TemplateEditResult;
    }

    export type CustomerNotificationsEventProps = TemplateEditedEventProps;
  }

  export namespace Webhooks {
    export interface WebhookToggledEventProps {
      userId: string;
      company: string;
      webhookCurrentlyActive: boolean;
    }

    export type WebhooksEventProps = WebhookToggledEventProps;
  }

  export type EventProps =
    | User.UserEventProps
    | Order.OrderEventProps
    | UI.UIEventProps
    | Route.RouteEventProps
    | Scheduling.SchedulingEventProps
    | Shopify.ShopifyEventProps
    | CustomerNotifications.CustomerNotificationsEventProps
    | Webhooks.WebhooksEventProps;
}
